import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetMarketRate } from '#/shared/api/generated/loadboard/theCargoLoadboard'
import type {
  ItemDispatch,
  ItemRoute,
  MarketRateItem,
  MarketRateRequest,
  MarketRateResponse,
  MarketSummary,
} from '#/shared/api/generated/loadboard/models'
import { formatDate } from '#/shared/lib/format'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'

import { MARKET_RATE_STATUSES } from './types'
import type { MarketRateRow, MarketRateStats, MarketRateStatus } from './types'

interface UseLaneSuggestedPriceResult {
  stats: MarketRateStats | undefined
  rows: MarketRateRow[]
  isLoading: boolean
  errorMessage: string | undefined
  fetch: (request: MarketRateRequest) => Promise<void>
  reset: () => void
}

const STATUS_BUCKETS: Record<string, MarketRateStatus> = {
  delivered: MARKET_RATE_STATUSES.DELIVERED,
  in_transit: MARKET_RATE_STATUSES.IN_TRANSIT,
  picked_up: MARKET_RATE_STATUSES.PICKED_UP,
  dispatched: MARKET_RATE_STATUSES.DISPATCHED,
  canceled: MARKET_RATE_STATUSES.CANCELED,
  cancelled: MARKET_RATE_STATUSES.CANCELED,
}

function fromSummary(summary: MarketSummary): MarketRateStats {
  return {
    low: summary.low.price,
    lowPerMi: summary.low.per_mile,
    recommended: summary.recommended.price,
    recommendedPerMi: summary.recommended.per_mile,
    high: summary.high.price,
    highPerMi: summary.high.per_mile,
    sampleSize: summary.sample_size,
    referenceDistanceMi: summary.reference_distance_miles,
  }
}

function formatLocation(point: ItemRoute['origin']): string {
  return `${point.city}, ${point.state}`
}

function normalizeStatus(raw: string): MarketRateStatus {
  return STATUS_BUCKETS[raw.toLowerCase()] ?? MARKET_RATE_STATUSES.LISTED
}

function dispatchFields(
  dispatch: ItemDispatch | undefined,
): Pick<
  MarketRateRow,
  | 'status'
  | 'statusLabel'
  | 'dispatchedDate'
  | 'actualPrice'
  | 'actualPricePerMi'
> {
  if (!dispatch) return { status: MARKET_RATE_STATUSES.LISTED }
  return {
    status: normalizeStatus(dispatch.status),
    statusLabel: dispatch.status_raw ?? undefined,
    dispatchedDate: dispatch.dispatched_at
      ? formatDate(dispatch.dispatched_at)
      : undefined,
    actualPrice: dispatch.price,
    actualPricePerMi: dispatch.per_mile,
  }
}

function toRow(item: MarketRateItem, index: number): MarketRateRow {
  const dispatch = item.dispatch ?? undefined
  return {
    id: item.id ?? `item-${index}`,
    vehicleType: item.vehicle_type,
    origin: formatLocation(item.route.origin),
    destination: formatLocation(item.route.destination),
    externalUrl: item.external_url ?? undefined,
    distanceMi: item.distance_miles ?? dispatch?.distance_miles ?? undefined,
    listedDate: item.listing.created_at
      ? formatDate(item.listing.created_at)
      : undefined,
    listedPrice: item.listing.price,
    listedPricePerMi: item.listing.per_mile,
    ...dispatchFields(dispatch),
  }
}

interface State {
  stats: MarketRateStats | undefined
  rows: MarketRateRow[]
  errorMessage: string | undefined
}

const INITIAL_STATE: State = {
  stats: undefined,
  rows: [],
  errorMessage: undefined,
}

export function useLaneSuggestedPrice(): UseLaneSuggestedPriceResult {
  const { t } = useTranslation()
  const mutation = useGetMarketRate()
  const [state, setState] = useState<State>(INITIAL_STATE)

  const apply = (response: MarketRateResponse) => {
    setState({
      stats: response.summary ? fromSummary(response.summary) : undefined,
      rows: response.items.map(toRow),
      errorMessage: undefined,
    })
  }

  const fetch = async (request: MarketRateRequest) => {
    setState(INITIAL_STATE)
    try {
      const result = await mutation.mutateAsync({ data: request })
      apply(result.data)
    } catch (error) {
      setState({
        stats: undefined,
        rows: [],
        errorMessage:
          getApiErrorDetail(error) ?? t('table.marketRate.fetchError'),
      })
    }
  }

  const reset = () => {
    setState(INITIAL_STATE)
  }

  return {
    ...state,
    isLoading: mutation.isPending,
    fetch,
    reset,
  }
}
